/**
 * Wasabi Cloud Storage Service
 * Handles file uploads to Wasabi S3-compatible storage
 */

import { S3Client, PutObjectCommand, DeleteObjectCommand, HeadBucketCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

// Configure AWS SDK v3 for Wasabi
const s3Client = new S3Client({
  credentials: {
    accessKeyId: process.env.REACT_APP_WASABI_ACCESS_KEY,
    secretAccessKey: process.env.REACT_APP_WASABI_SECRET_KEY,
  },
  endpoint: process.env.REACT_APP_WASABI_ENDPOINT,
  region: process.env.REACT_APP_WASABI_REGION,
  forcePathStyle: true,
});

const bucketName = process.env.REACT_APP_WASABI_BUCKET;

/**
 * Upload a file to Wasabi storage
 * @param {File} file - The file to upload
 * @param {string} folder - The folder path (e.g., 'props', '3d-models', 'pdfs')
 * @param {string} propId - The prop ID for naming convention
 * @param {string} suffix - Optional suffix (e.g., 'main', 'side', 'detail')
 * @returns {Promise<Object>} Upload result with URL and key
 */
export const uploadToWasabi = async (file, folder, propId, suffix = 'main') => {
  if (!file) throw new Error('No file provided');
  if (!propId) throw new Error('Prop ID is required');

  // Generate file key with timestamp for uniqueness
  const timestamp = Date.now();
  const fileExtension = file.name.split('.').pop().toLowerCase();
  const key = `${folder}/${propId}_${suffix}_${timestamp}.${fileExtension}`;

  // Convert File to ArrayBuffer for AWS SDK v3 compatibility
  const arrayBuffer = await file.arrayBuffer();
  
  const command = new PutObjectCommand({
    Bucket: bucketName,
    Key: key,
    Body: new Uint8Array(arrayBuffer),
    ContentType: file.type,
    // Remove ACL: 'public-read' since public access is disabled
    Metadata: {
      'prop-id': propId,
      'upload-timestamp': timestamp.toString(),
      'original-name': file.name
    }
  });

  try {
    console.log('Uploading to Wasabi:', { key, contentType: file.type, size: file.size });
    
    const result = await s3Client.send(command);
    
    // Generate a signed URL that expires in 30 days (for better stability)
    const signedUrl = await getWasabiSignedUrl(key, 30 * 24 * 3600); // 30 days
    
    return {
      success: true,
      url: signedUrl, // Use signed URL instead of public URL
      key: key,
      bucket: bucketName,
      etag: result.ETag,
      size: file.size,
      contentType: file.type,
      originalName: file.name
    };
  } catch (error) {
    console.error('Wasabi upload error:', error);
    throw new Error(`Upload failed: ${error.message}`);
  }
};

/**
 * Upload multiple images for a prop
 * @param {Array<File>} files - Array of image files
 * @param {string} propId - The prop ID
 * @returns {Promise<Array>} Array of upload results
 */
export const uploadPropImages = async (files, propId) => {
  if (!files || files.length === 0) {
    throw new Error('No files provided');
  }

  const uploadPromises = files.map((file, index) => {
    const suffix = index === 0 ? 'main' : `image${index + 1}`;
    return uploadToWasabi(file, 'props', propId, suffix);
  });

  try {
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    console.error('Batch upload error:', error);
    throw error;
  }
};

/**
 * Delete a file from Wasabi storage
 * @param {string} key - The file key to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteFromWasabi = async (key) => {
  const command = new DeleteObjectCommand({
    Bucket: bucketName,
    Key: key
  });

  try {
    await s3Client.send(command);
    console.log('File deleted from Wasabi:', key);
    return true;
  } catch (error) {
    console.error('Delete error:', error);
    throw new Error(`Delete failed: ${error.message}`);
  }
};

/**
 * Generate a signed URL for private file access (future use)
 * @param {string} key - The file key
 * @param {number} expiresIn - URL expiration time in seconds (default 1 hour)
 * @returns {Promise<string>} Signed URL
 */
export const getWasabiSignedUrl = async (key, expiresIn = 3600) => {
  const command = new GetObjectCommand({
    Bucket: bucketName,
    Key: key
  });

  return await getSignedUrl(s3Client, command, { expiresIn });
};

/**
 * Check if Wasabi service is properly configured
 * @returns {boolean} Configuration status
 */
export const isWasabiConfigured = () => {
  return !!(
    process.env.REACT_APP_WASABI_ACCESS_KEY &&
    process.env.REACT_APP_WASABI_SECRET_KEY &&
    process.env.REACT_APP_WASABI_BUCKET &&
    process.env.REACT_APP_WASABI_ENDPOINT
  );
};

/**
 * Test Wasabi connection
 * @returns {Promise<boolean>} Connection status
 */
export const testWasabiConnection = async () => {
  try {
    const command = new HeadBucketCommand({ Bucket: bucketName });
    await s3Client.send(command);
    console.log('Wasabi connection successful');
    return true;
  } catch (error) {
    console.error('Wasabi connection failed:', error);
    return false;
  }
};
